import React, { useState, useEffect, useContext, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ApiURL from '../Api/Api';
import Navbar from './Navbar';
import { SidebarContext } from './SidebarContext';
import Sidebar from './Sidebar';
import {
  MagnifyingGlassIcon,
  DocumentArrowDownIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  XMarkIcon,
  UserCircleIcon,
  UsersIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  CheckCircleIcon,
  XCircleIcon,
  TableCellsIcon,
  Squares2X2Icon,
  ChevronRightIcon,
  ShieldCheckIcon,
  WrenchScrewdriverIcon,
  BriefcaseIcon,
} from '@heroicons/react/24/outline';

/* ── helpers ─────────────────────────────────────────────────── */
const fmtDate = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/* ── role badge ──────────────────────────────────────────────── */
const RoleBadge = ({ role }) => {
  const map = {
    company_owner: { label: 'Owner',  cls: 'bg-amber-50 text-amber-700 border-amber-200',   icon: BriefcaseIcon },
    admin:         { label: 'Admin',  cls: 'bg-brand-50 text-brand-700 border-brand-200', icon: ShieldCheckIcon },
    worker:        { label: 'Worker', cls: 'bg-slate-50 text-slate-600 border-slate-200',    icon: WrenchScrewdriverIcon },
  };
  const { label, cls, icon: Icon } = map[role] || map.worker;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full border ${cls}`}>
      <Icon className="w-2.5 h-2.5" /> {label}
    </span>
  );
};

/* ── status badge ────────────────────────────────────────────── */
const StatusBadge = ({ status }) =>
  (status || 'active') === 'active' ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-full border border-emerald-200">
      <CheckCircleIcon className="w-2.5 h-2.5" /> Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-50 text-slate-500 text-[10px] font-bold rounded-full border border-slate-200">
      <XCircleIcon className="w-2.5 h-2.5" /> Inactive
    </span>
  );

/* ── avatar initials ─────────────────────────────────────────── */
const Avatar = ({ name, role }) => {
  const colors = {
    company_owner: 'bg-amber-100 text-amber-700',
    admin:         'bg-brand-100 text-brand-700',
    worker:        'bg-slate-100 text-slate-600',
  };
  return (
    <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-bold text-sm flex-shrink-0 ${colors[role] || colors.worker}`}>
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );
};

/* ── field label ─────────────────────────────────────────────── */
const FL = ({ children }) => (
  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{children}</label>
);

const inputCls = 'w-full h-9 px-3 text-[12px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100 transition-all placeholder:text-slate-300';
const selectCls = inputCls + ' appearance-none cursor-pointer';

/* ══════════════════════════════════════════════════════════════ */
const UserReport = () => {
  const [users, setUsers]               = useState([]);
  const [searchName, setSearchName]     = useState('');
  const [searchDate, setSearchDate]     = useState('');
  const [isLoading, setIsLoading]       = useState(true);
  const [error, setError]               = useState('');
  const [siteOptions, setSiteOptions]   = useState([]);
  const [editingUser, setEditingUser]   = useState(null);
  const [editForm, setEditForm]         = useState({ user_name: '', email: '', phone: '', role: 'worker', account_status: 'active', assigned_sites: [] });
  const [isSaving, setIsSaving]         = useState(false);
  const [viewMode, setViewMode]         = useState('table'); // 'table' | 'cards'
  const [roleFilter, setRoleFilter]     = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const { isSidebarOpen } = useContext(SidebarContext);
  const companyId = localStorage.getItem('company_id') || '';
  const actorId   = localStorage.getItem('id') || '';
  const actorRole = localStorage.getItem('role') || '';

  /* ── fetch ──────────────────────────────────────────────────── */
  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const res  = await fetch(`${ApiURL}/getUserReport.php?company_id=${companyId}&actor_id=${actorId}&actor_role=${actorRole}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const rows = Array.isArray(data) ? data : [];
      setUsers(rows);
    } catch (e) { setError(`Network error: ${e.message}`); }
    finally { setIsLoading(false); }
  };

  const fetchSites = async () => {
    try {
      const res  = await fetch(`${ApiURL}/getSites.php?company_id=${companyId}&user_id=${actorId}`);
      const data = await res.json();
      if (data.status === 'success') setSiteOptions(data.sites || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchUsers(); }, []);
  useEffect(() => { if (companyId && actorId) fetchSites(); }, [companyId, actorId]);

  /* ── filtered list ──────────────────────────────────────────── */
  const filtered = useMemo(() => {
    return users.filter(u => {
      const nameMatch = (u.user_name || '').toLowerCase().includes(searchName.toLowerCase());
      const dateMatch = !searchDate || (u.created_date || '').includes(searchDate);
      const roleMatch = roleFilter === 'all' || u.role === roleFilter;
      const statusMatch = statusFilter === 'all' || (u.account_status || 'active') === statusFilter;
      return nameMatch && dateMatch && roleMatch && statusMatch;
    });
  }, [users, searchName, searchDate, roleFilter, statusFilter]);

  /* ── stats ──────────────────────────────────────────────────── */
  const stats = useMemo(() => ({
    total:   users.length,
    active:  users.filter(u => (u.account_status || 'active') === 'active').length,
    admins:  users.filter(u => u.role === 'admin').length,
    workers: users.filter(u => u.role === 'worker').length,
  }), [users]);

  /* ── edit ───────────────────────────────────────────────────── */
  const openEdit = (user) => {
    const sites = Array.isArray(user.assigned_sites)
      ? user.assigned_sites
      : String(user.assigned_sites || '').split(',').map(s => s.trim()).filter(Boolean);
    setEditingUser(user);
    setEditForm({ user_name: user.user_name || '', email: user.email || user.email_id || '', phone: user.phone || '', role: user.role || 'worker', account_status: user.account_status || 'active', assigned_sites: sites });
    setError('');
  };

  const closeEdit = () => {
    setEditingUser(null);
    setEditForm({ user_name: '', email: '', phone: '', role: 'worker', account_status: 'active', assigned_sites: [] });
  };

  const saveUser = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');
    try {
      const res    = await fetch(`${ApiURL}/update_user.php`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: editingUser.id, actor_id: actorId, actor_role: actorRole, ...editForm }) });
      const result = await res.json();
      if (result.status !== 'success') throw new Error(result.message || 'Failed to update');
      closeEdit(); fetchUsers();
    } catch (e) { setError(e.message || 'Failed to update user'); }
    finally { setIsSaving(false); }
  };

  /* ── export ─────────────────────────────────────────────────── */
  const exportCSV = () => {
    const headers = ['User Name','Email','Phone','Role','Status','Assigned Sites','Created By','Created Date'];
    const rows = filtered.map(u => [
      u.user_name||'', u.email||u.email_id||'', u.phone||'', u.role||'',
      u.account_status||'active',
      Array.isArray(u.assigned_sites) ? u.assigned_sites.join(' | ') : (u.assigned_sites||''),
      u.created_by||'', u.created_date||'',
    ]);
    const csv = [headers,...rows].map(r => r.map(c => { const v = String(c); return v.includes(',') || v.includes('"') ? `"${v.replace(/"/g,'""')}"` : v; }).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    a.download = 'UserReport.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const roles = ['all','company_owner','admin','worker'];
  const roleLabels = { all: 'All', company_owner: 'Owners', admin: 'Admins', worker: 'Workers' };
  const statuses = ['all', 'active', 'inactive'];
  const statusLabels = { all: 'All Status', active: 'Active', inactive: 'Inactive' };

  return (
    <div className="app-shell min-h-screen">
      <Navbar />
      <div className="flex pt-16">
        <Sidebar />
        <motion.main
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
          className={`flex-1 min-h-screen transition-all duration-300 min-w-0 ${isSidebarOpen ? 'lg:ml-72' : ''}`}
        >
          <div className="max-w-7xl mx-auto px-3 py-4 sm:px-5 sm:py-5 lg:px-6 lg:py-6">

            {/* ── Page header ──────────────────────────────────── */}
            <div className="page-surface flex items-center justify-between gap-3 mb-5 p-4 sm:p-5">
              <div>
                <h1 className="text-lg font-bold text-slate-800 leading-tight">User Report</h1>
                <p className="text-[11px] text-slate-500 mt-0.5">{filtered.length} of {users.length} users</p>
              </div>
              <div className="flex items-center gap-2">
                {/* View toggle */}
                <div className="hidden sm:flex items-center bg-slate-100 border border-slate-200 rounded-lg p-1">
                  {[{ v: 'table', icon: TableCellsIcon }, { v: 'cards', icon: Squares2X2Icon }].map(({ v, icon: Ic }) => (
                    <button key={v} onClick={() => setViewMode(v)}
                      className={`w-8 h-7 rounded-md flex items-center justify-center transition-all ${viewMode === v ? 'bg-brand-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                      <Ic className="w-3.5 h-3.5" />
                    </button>
                  ))}
                </div>
                <button onClick={exportCSV} disabled={!filtered.length}
                  className="pro-button pro-button-secondary h-8 px-3 text-xs disabled:opacity-40">
                  <DocumentArrowDownIcon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Export</span>
                </button>
                <button onClick={fetchUsers}
                  className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-brand-700 shadow-sm transition-all">
                  <ArrowPathIcon className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {/* ── Stats row ────────────────────────────────────── */}
            <div className="grid grid-cols-4 gap-2 sm:gap-3 mb-4">
              {[
                { label: 'Total',   value: stats.total,   iconBg: 'bg-brand-600', icon: UsersIcon,          text: 'text-brand-700',  bg: 'bg-white border border-slate-200' },
                { label: 'Active',  value: stats.active,  iconBg: 'bg-emerald-500', icon: CheckCircleIcon,   text: 'text-emerald-700', bg: 'bg-emerald-50' },
                { label: 'Admins',  value: stats.admins,  iconBg: 'bg-amber-500', icon: ShieldCheckIcon,    text: 'text-slate-800',  bg: 'bg-white border border-slate-200' },
                { label: 'Workers', value: stats.workers, iconBg: 'bg-slate-400',  icon: WrenchScrewdriverIcon, text: 'text-slate-700', bg: 'bg-slate-50' },
              ].map(({ label, value, iconBg, icon: Icon, text, bg }, i) => (
                <motion.div key={label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className={`${bg} rounded-lg p-3 sm:p-4 flex items-center gap-2 sm:gap-3 shadow-sm`}>
                  <div className={`${iconBg} w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className={`text-base sm:text-lg font-bold ${text} leading-tight`}>{value}</p>
                    <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">{label}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* ── Search + filter bar ──────────────────────────── */}
            <div className="pro-card p-3 sm:p-4 mb-4">
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                {/* Name search */}
                <div className="relative flex-1">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-300 pointer-events-none" />
                  <input type="text" value={searchName} onChange={e => setSearchName(e.target.value)}
                    placeholder="Search by name..." className={`${inputCls} pl-9`} />
                </div>
                {/* Date search */}
                <div className="relative sm:w-48">
                  <CalendarDaysIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-300 pointer-events-none" />
                  <input type="text" value={searchDate} onChange={e => setSearchDate(e.target.value)}
                    placeholder="Date (YYYY-MM-DD)" className={`${inputCls} pl-9`} />
                </div>
                {/* Role filter pills */}
                <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-lg p-1 flex-shrink-0 overflow-x-auto">
                  {roles.map(r => (
                    <button key={r} onClick={() => setRoleFilter(r)}
                      className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all whitespace-nowrap ${
                        roleFilter === r ? 'bg-brand-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                      }`}>
                      {roleLabels[r]}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-lg p-1 flex-shrink-0 overflow-x-auto">
                  {statuses.map(s => (
                    <button key={s} onClick={() => setStatusFilter(s)}
                      className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all whitespace-nowrap ${
                        statusFilter === s ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                      }`}>
                      {statusLabels[s]}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Error ────────────────────────────────────────── */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl px-4 py-3">{error}</div>
            )}

            {/* ── Loading skeletons ────────────────────────────── */}
            {isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-xl h-14 animate-pulse border border-slate-100" />
                ))}
              </div>

            ) : filtered.length === 0 ? (
              <div className="pro-card py-16 flex flex-col items-center gap-3">
                <UsersIcon className="w-10 h-10 text-slate-200" />
                <p className="text-sm font-semibold text-slate-400">No users found</p>
                {(searchName || searchDate || roleFilter !== 'all') && (
                  <button onClick={() => { setSearchName(''); setSearchDate(''); setRoleFilter('all'); }}
                    className="text-xs text-brand-600 font-semibold">Clear filters</button>
                )}
              </div>

            ) : (
              <>
                {/* ── MOBILE: always card view ──────────────────── */}
                <div className="block sm:hidden space-y-2">
                  {filtered.map((user, i) => (
                    <motion.div key={user.id || i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(i * 0.03, 0.3) }}
                      className="pro-card overflow-hidden">
                      {/* Role color strip */}
                      <div className={`h-0.5 w-full ${user.role === 'admin' ? 'bg-brand-600' : user.role === 'company_owner' ? 'bg-amber-500' : 'bg-slate-300'}`} />
                      <div className="px-4 py-3.5">
                        {/* Top row */}
                        <div className="flex items-center justify-between gap-3 mb-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <Avatar name={user.user_name} role={user.role} />
                            <div className="min-w-0">
                              <p className="text-[13px] font-bold text-slate-800 truncate">{user.user_name}</p>
                              <p className="text-[11px] text-slate-400 truncate">{user.email || user.email_id || '-'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <StatusBadge status={user.account_status} />
                          </div>
                        </div>

                        {/* Info grid */}
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <div>
                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Role</p>
                            <RoleBadge role={user.role} />
                          </div>
                          <div>
                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Phone</p>
                            <p className="text-[12px] font-medium text-slate-700">{user.phone || '-'}</p>
                          </div>
                          <div>
                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Created by</p>
                            <p className="text-[12px] font-medium text-slate-700 truncate">{user.created_by || '-'}</p>
                          </div>
                          <div>
                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Date</p>
                            <p className="text-[12px] font-medium text-slate-700">{fmtDate(user.created_date)}</p>
                          </div>
                        </div>

                        {/* Sites */}
                        {(Array.isArray(user.assigned_sites) ? user.assigned_sites : String(user.assigned_sites||'').split(',').map(s=>s.trim()).filter(Boolean)).length > 0 && (
                          <div className="mb-3">
                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Assigned Sites</p>
                            <div className="flex flex-wrap gap-1">
                              {(Array.isArray(user.assigned_sites) ? user.assigned_sites : String(user.assigned_sites||'').split(',').map(s=>s.trim()).filter(Boolean)).map(s => (
                                <span key={s} className="px-2 py-0.5 bg-brand-50 text-brand-700 text-[10px] font-semibold rounded-full border border-brand-100">
                                  {s}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Edit */}
                        <button onClick={() => openEdit(user)}
                          className="w-full h-8 bg-brand-50 border border-brand-200 text-brand-700 text-[11px] font-bold rounded-lg hover:bg-brand-100 transition-colors flex items-center justify-center gap-1.5">
                          <PencilSquareIcon className="w-3.5 h-3.5" /> Edit User
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* ── DESKTOP: table (default) ──────────────────── */}
                {viewMode === 'table' && (
                  <div className="hidden sm:block pro-card overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-[12px] text-left border-collapse min-w-[860px]">
                        <thead>
                          <tr className="border-b border-slate-100 bg-slate-50/80">
                            {['User','Email','Phone','Role','Status','Sites','Created By','Date',''].map(h => (
                              <th key={h} className="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {filtered.map((user, i) => {
                            const sites = Array.isArray(user.assigned_sites)
                              ? user.assigned_sites
                              : String(user.assigned_sites||'').split(',').map(s=>s.trim()).filter(Boolean);
                            return (
                              <motion.tr key={user.id||i} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                                transition={{ delay: Math.min(i * 0.02, 0.2) }}
                                className="hover:bg-slate-50/60 transition-colors">
                                {/* User */}
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2.5">
                                    <Avatar name={user.user_name} role={user.role} />
                                    <span className="font-semibold text-slate-800 whitespace-nowrap">{user.user_name}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-slate-500 max-w-[150px] truncate">{user.email||user.email_id||'-'}</td>
                                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{user.phone||'-'}</td>
                                <td className="px-4 py-3"><RoleBadge role={user.role} /></td>
                                <td className="px-4 py-3"><StatusBadge status={user.account_status} /></td>
                                <td className="px-4 py-3">
                                  <div className="flex flex-wrap gap-1 max-w-[160px]">
                                    {sites.length > 0
                                      ? sites.slice(0, 2).map(s => (
                                          <span key={s} className="px-2 py-0.5 bg-brand-50 text-brand-700 text-[10px] font-semibold rounded-full border border-brand-100 whitespace-nowrap">{s}</span>
                                        ))
                                      : <span className="text-slate-400">-</span>
                                    }
                                    {sites.length > 2 && <span className="text-[10px] text-slate-400 font-semibold">+{sites.length - 2}</span>}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{user.created_by||'-'}</td>
                                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(user.created_date)}</td>
                                <td className="px-4 py-3">
                                  <button onClick={() => openEdit(user)}
                                    className="w-7 h-7 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 hover:text-brand-700 hover:border-brand-200 hover:bg-brand-50 transition-all">
                                    <PencilSquareIcon className="w-3.5 h-3.5" />
                                  </button>
                                </td>
                              </motion.tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/40 flex items-center justify-between">
                      <p className="text-[11px] text-slate-400">{filtered.length} users</p>
                      <button onClick={exportCSV} className="flex items-center gap-1.5 text-[11px] font-bold text-brand-600 hover:text-brand-700">
                        <DocumentArrowDownIcon className="w-3.5 h-3.5" /> Export CSV
                      </button>
                    </div>
                  </div>
                )}

                {/* ── DESKTOP: card grid ────────────────────────── */}
                {viewMode === 'cards' && (
                  <div className="hidden sm:grid grid-cols-2 lg:grid-cols-3 gap-3">
                    {filtered.map((user, i) => {
                      const sites = Array.isArray(user.assigned_sites)
                        ? user.assigned_sites
                        : String(user.assigned_sites||'').split(',').map(s=>s.trim()).filter(Boolean);
                      return (
                        <motion.div key={user.id||i} initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: Math.min(i * 0.03, 0.25) }}
                          className="pro-card overflow-hidden">
                          <div className={`h-0.5 w-full ${user.role === 'admin' ? 'bg-brand-600' : user.role === 'company_owner' ? 'bg-amber-500' : 'bg-slate-300'}`} />
                          <div className="p-4">
                            <div className="flex items-start justify-between gap-2 mb-3">
                              <div className="flex items-center gap-2.5">
                                <Avatar name={user.user_name} role={user.role} />
                                <div>
                                  <p className="text-[13px] font-bold text-slate-800">{user.user_name}</p>
                                  <p className="text-[11px] text-slate-400 truncate max-w-[140px]">{user.email||user.email_id||'-'}</p>
                                </div>
                              </div>
                              <StatusBadge status={user.account_status} />
                            </div>

                            <div className="flex flex-wrap gap-1.5 mb-3">
                              <RoleBadge role={user.role} />
                              {user.phone && (
                                <span className="px-2 py-0.5 bg-slate-50 text-slate-500 text-[10px] font-semibold rounded-full border border-slate-200">{user.phone}</span>
                              )}
                            </div>

                            {sites.length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-3">
                                {sites.slice(0, 3).map(s => (
                                  <span key={s} className="px-2 py-0.5 bg-brand-50 text-brand-700 text-[10px] font-semibold rounded-full border border-brand-100">{s}</span>
                                ))}
                                {sites.length > 3 && <span className="text-[10px] text-slate-400 font-semibold self-center">+{sites.length - 3}</span>}
                              </div>
                            )}

                            <div className="flex items-center justify-between pt-2 border-t border-slate-50">
                              <p className="text-[10px] text-slate-400">{user.created_by||'-'} / {fmtDate(user.created_date)}</p>
                              <button onClick={() => openEdit(user)}
                                className="flex items-center gap-1 text-[11px] font-bold text-brand-600 hover:text-brand-700 transition-colors">
                                <PencilSquareIcon className="w-3 h-3" /> Edit
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </motion.main>
      </div>

      {/* ── Edit modal ─────────────────────────────────────────── */}
      <AnimatePresence>
        {editingUser && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
            onClick={e => e.target === e.currentTarget && closeEdit()}>
            <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
              className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-lg overflow-hidden shadow-2xl border border-slate-200">

              {/* Modal header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <Avatar name={editingUser.user_name} role={editingUser.role} />
                  <div>
                    <p className="text-sm font-bold text-slate-800">Edit User</p>
                    <p className="text-[11px] text-slate-400">{editingUser.user_name}</p>
                  </div>
                </div>
                <button onClick={closeEdit}
                  className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors">
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>

              <form onSubmit={saveUser} className="px-5 py-4 overflow-y-auto max-h-[75vh]">
                {error && <div className="mb-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl px-3 py-2">{error}</div>}

                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="col-span-2">
                    <FL>User Name</FL>
                    <input type="text" required value={editForm.user_name}
                      onChange={e => setEditForm(p=>({...p,user_name:e.target.value}))} className={inputCls} />
                  </div>
                  <div>
                    <FL>Email</FL>
                    <input type="email" value={editForm.email}
                      onChange={e => setEditForm(p=>({...p,email:e.target.value}))} className={inputCls} />
                  </div>
                  <div>
                    <FL>Phone</FL>
                    <input type="text" value={editForm.phone}
                      onChange={e => setEditForm(p=>({...p,phone:e.target.value}))} className={inputCls} />
                  </div>
                  {actorRole === 'company_owner' && editingUser.role !== 'company_owner' && (
                    <div className="col-span-2">
                      <FL>Role</FL>
                      <select
                        value={editForm.role}
                        onChange={e => setEditForm(p=>({...p,role:e.target.value}))}
                        className={selectCls}
                      >
                        <option value="admin">Admin</option>
                        <option value="worker">Worker</option>
                      </select>
                    </div>
                  )}
                  <div className="col-span-2">
                    <FL>Account Status</FL>
                    <div className="flex gap-2">
                      {['active','inactive'].map(s => (
                        <button key={s} type="button" onClick={() => setEditForm(p=>({...p,account_status:s}))}
                          className={`flex-1 h-9 rounded-xl text-xs font-bold border transition-all ${
                            editForm.account_status === s
                              ? s === 'active' ? 'bg-emerald-500 text-white border-emerald-500 shadow-sm' : 'bg-slate-500 text-white border-slate-500 shadow-sm'
                              : 'bg-slate-50 text-slate-500 border-slate-200 hover:border-slate-300'
                          }`}>
                          {s.charAt(0).toUpperCase() + s.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Sites multiselect */}
                <div className="mb-4">
                  <FL>Assigned Sites</FL>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-2 max-h-36 overflow-y-auto space-y-1">
                    {siteOptions.length === 0 ? (
                      <p className="text-[11px] text-slate-400 text-center py-3">No sites available</p>
                    ) : siteOptions.map(site => {
                      const checked = editForm.assigned_sites.includes(site.site_name);
                      return (
                        <label key={site.site_id}
                          className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-all ${checked ? 'bg-brand-50 border border-brand-200' : 'hover:bg-slate-100'}`}>
                          <input type="checkbox" checked={checked} className="w-3.5 h-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                            onChange={e => setEditForm(p => ({
                              ...p,
                              assigned_sites: e.target.checked
                                ? [...p.assigned_sites, site.site_name]
                                : p.assigned_sites.filter(s => s !== site.site_name),
                            }))} />
                          <span className={`text-[12px] font-medium ${checked ? 'text-brand-700' : 'text-slate-600'}`}>{site.site_name}</span>
                          {checked && <span className="ml-auto text-[10px] text-brand-600 font-bold">Selected</span>}
                        </label>
                      );
                    })}
                  </div>
                  {editForm.assigned_sites.length > 0 && (
                    <p className="text-[10px] text-brand-600 font-semibold mt-1">{editForm.assigned_sites.length} site{editForm.assigned_sites.length !== 1 ? 's' : ''} selected</p>
                  )}
                </div>

                <div className="flex gap-2 pt-3 border-t border-slate-100">
                  <button type="button" onClick={closeEdit}
                    className="flex-1 h-10 rounded-xl border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-50 transition-colors">
                    Cancel
                  </button>
                  <button type="submit" disabled={isSaving}
                    className="pro-button pro-button-primary flex-1 h-10 text-xs disabled:opacity-50 disabled:cursor-not-allowed">
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserReport;
